import type { IconName } from 'lucide-react/dynamic';
import { DateTime } from 'luxon';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  default as api,
  RecurrenceTaskStatusEnum,
  type Card,
  type Recurrence,
  type RecurrenceTaskItem,
  type RecurrenceTaskStatus,
} from '../service/api';
import { createPersistStorage } from './utils/persist';

interface TaskItem {
  _id: string;
  status: RecurrenceTaskStatus;
  recurrenceId: string;
  title: string;
  description?: string;
  start: DateTime;
  stop: DateTime;
  rewardCard: Card | string;
  penaltyCard?: Card | string;
  assignedTo: string;
  progress: number;
  icon: IconName;
  isCompleted: boolean;
  isActive: boolean;
}

interface GroupedTasks {
  today: TaskItem[];
  thisWeek: TaskItem[];
}

interface TasksState {
  tasks: GroupedTasks;
  isLoading: boolean;
  error: string | null;
}

interface TasksActions {
  fetchTasks: () => Promise<void>;
  clearTasks: () => void;
  completeTask: (_id: string, recurrenceId: string) => Promise<void>;
  getTaskById: (taskId: string) => TaskItem | undefined;
}

type TasksStore = TasksState & TasksActions;

type TasksStorePersist = Pick<TasksStore, 'tasks'>;

function convertToTaskItem(
  task: RecurrenceTaskItem,
  recurrence: Recurrence,
  currentUserId: string,
): TaskItem {
  const now = DateTime.now();

  const totalDuration = task.stop.diff(task.start).milliseconds;
  const elapsedTime = now.diff(task.start).milliseconds;

  let progress = 0;
  let isActive = false;

  if (now >= task.stop) {
    progress = 100;
  } else if (now > task.start) {
    isActive = true;
    progress = Math.round((elapsedTime / totalDuration) * 100);
  }

  return {
    _id: task._id,
    recurrenceId: recurrence._id,
    title: recurrence.title,
    description: recurrence.description,
    start: task.start,
    stop: task.stop,
    status: task.status,
    rewardCard: recurrence.rewardCard,
    penaltyCard: recurrence.penaltyCard,
    assignedTo: currentUserId,
    progress,
    isActive,
    isCompleted: task.isCompleted,
    icon: recurrence.icon,
  };
}

const useTasksStore = create<TasksStore>()(
  persist<TasksStore, [], [], TasksStorePersist>(
    (set, get) => ({
      tasks: {
        today: [],
        thisWeek: [],
      },
      isLoading: false,
      error: null,

      fetchTasks: async () => {
        const hasCachedTasks =
          get().tasks.today.length > 0 || get().tasks.thisWeek.length > 0;
        set({ isLoading: !hasCachedTasks, error: null });

        try {
          const now = DateTime.now();
          const startOfWeek = now.startOf('week');
          const endOfWeek = now.endOf('week');

          const response = await api.tasksGet(startOfWeek, endOfWeek);

          const startOfToday = now.startOf('day');
          const endOfToday = now.endOf('day');

          const todayTasks: TaskItem[] = [];
          const weekTasks: TaskItem[] = [];

          // Get current user ID - we need to know which user is viewing
          const currentUser = await api.userCurrentGet();
          const currentUserId = currentUser._id;

          response.forEach(({ recurrence, tasks }) => {
            // Filter tasks to get only today's task if multiple exist
            const todaysTask = tasks.find(
              task => task.start >= startOfToday && task.stop <= endOfToday,
            );

            if (todaysTask) {
              todayTasks.push(
                convertToTaskItem(todaysTask, recurrence, currentUserId),
              );
              return;
            }

            const weekTasksSelected = tasks
              .filter(t => t.start > now && t.stop <= endOfWeek)
              .map(task => convertToTaskItem(task, recurrence, currentUserId));

            if (weekTasksSelected?.length) {
              weekTasks.push(...weekTasksSelected);
              return;
            }
          });

          set({
            tasks: {
              today: todayTasks.sort(
                (a, b) => a.stop.toMillis() - b.stop.toMillis(),
              ),
              thisWeek: weekTasks.sort(
                (a, b) => a.stop.toMillis() - b.stop.toMillis(),
              ),
            },
            isLoading: false,
          });
        } catch (error) {
          set({
            error:
              error instanceof Error ? error.message : 'Failed to fetch tasks',
            isLoading: false,
          });
        }
      },

      completeTask: async (_id: string, recurrenceId: string) => {
        try {
          await api.taskCompleteRequest(_id, recurrenceId);

          // Update local state - mark task as completed
          set(state => {
            const updateTaskProgress = (tasks: TaskItem[]) =>
              tasks.map(task =>
                task._id === _id
                  ? {
                      ...task,
                      status: RecurrenceTaskStatusEnum.CompleteRequest,
                      isActive: false,
                    }
                  : task,
              );

            return {
              tasks: {
                today: updateTaskProgress(state.tasks.today),
                thisWeek: updateTaskProgress(state.tasks.thisWeek),
              },
            };
          });
        } catch (error) {
          throw new Error(
            error instanceof Error ? error.message : 'Failed to complete task',
          );
        }
      },

      getTaskById: (taskId: string) => {
        const allTasks = [...get().tasks.today, ...get().tasks.thisWeek];
        return allTasks.find(task => task._id === taskId);
      },

      clearTasks: () =>
        set({
          tasks: { today: [], thisWeek: [] },
          error: null,
        }),
    }),
    {
      name: 'tasks-store',
      storage: createPersistStorage<TasksStorePersist>(),
      partialize: state => ({
        tasks: state.tasks,
      }),
      onRehydrateStorage: () => state => {
        if (!state) return;

        const now = DateTime.now();
        const startOfCurrentWeek = now.startOf('week');
        const endOfCurrentWeek = now.endOf('week');

        // Check if any task is outside the current week
        const hasOutdatedTasks = [
          ...state.tasks.today,
          ...state.tasks.thisWeek,
        ].some(
          task =>
            task.start < startOfCurrentWeek || task.start > endOfCurrentWeek,
        );

        // Clear tasks if they belong to a different week
        if (hasOutdatedTasks) {
          state.tasks = {
            today: [],
            thisWeek: [],
          };
        }
      },
    },
  ),
);

export default useTasksStore;
export type { GroupedTasks, TaskItem };
